// SPDX-License-Identifier: BUSL-1.1
/**
 * Dashboard V2 — redesigned operations command center.
 *
 * Shell that composes the Wave 1 primitives (topbar + status cards +
 * quick actions + activity feed + external links footer) behind the
 * `dashboard-v2` feature flag.
 *
 * Wave 2 (DASH-11 through DASH-18) will replace the status card
 * placeholders with real per-resource summary queries.
 * Wave 3 (DASH-19 through DASH-23) will add the Row 4 metrics cards
 * (agent cost, config versions, prompt cache savings).
 */

import type * as React from 'react';
import { useQueryClient, useQuery } from '@tanstack/react-query';
import { Link } from 'react-router';
import { Bot, Layers, ToggleLeft, ShieldCheck, type LucideIcon } from 'lucide-react';
import { apiFetch, API_BASE } from '@/lib/api-client';
import { DashboardTopbar } from '@/components/dashboard-topbar';
import { QuickActionsCard } from '@/components/quick-actions-card';
import { ActivityFeed } from '@/components/activity-feed';
import { ExternalLinksFooter } from '@/components/external-links-footer';
import { FlagHealthCard } from '@/components/flag-health-card';
import { FlagLifecycleCard } from '@/components/flag-lifecycle-card';
import { FlagRecentChanges } from '@/components/flag-recent-changes';
import { GovernanceSessionsCard } from '@/components/governance-sessions-card';
import { GovernanceGatesCard } from '@/components/governance-gates-card';
import { GovernanceBudgetCard } from '@/components/governance-budget-card';
import { GovernanceModerationCard } from '@/components/governance-moderation-card';
import { HealthPill } from '@/components/health-pill';
import { type HealthResponse } from '@/lib/health';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { DashboardSkeleton } from '@/components/skeletons';
import type { AuditResponse, Flag, ConfigSurface } from '@/types/api';

/* ------------------------------------------------------------------ */
/*  Status Card — lightweight wrapper, no Wave 2 summary data yet     */
/* ------------------------------------------------------------------ */

interface StatusCardProps {
  title: string;
  icon: LucideIcon;
  iconColor: 'purple' | 'blue' | 'green' | 'amber';
  href?: string;
  children: React.ReactNode;
}

const ICON_COLORS: Record<StatusCardProps['iconColor'], string> = {
  purple: 'bg-purple-950/40 text-purple-400',
  blue: 'bg-blue-950/40 text-blue-400',
  green: 'bg-green-950/40 text-green-400',
  amber: 'bg-amber-950/40 text-amber-400',
};

function StatusCard({ title, icon: Icon, iconColor, href, children }: StatusCardProps) {
  const inner = (
    <Card className="transition hover:-translate-y-px hover:border-neutral-700">
      <CardHeader className="flex flex-row items-start justify-between space-y-0 pb-2">
        <CardTitle className="text-[11px] font-semibold uppercase tracking-wider text-neutral-500">
          {title}
        </CardTitle>
        <div className={`flex size-8 items-center justify-center rounded-md ${ICON_COLORS[iconColor]}`}>
          <Icon className="size-4" />
        </div>
      </CardHeader>
      <CardContent>{children}</CardContent>
    </Card>
  );
  if (!href) return inner;
  // Absolute URLs (e.g. the System Health card pointing at the Worker
  // origin) must use a raw `<a target="_blank">` — React Router's Link
  // would intercept the click and try to match the href against SPA
  // routes, falling through to the catchall. See PR #42 for the incident.
  if (/^https?:\/\//.test(href)) {
    return (
      <a href={href} target="_blank" rel="noopener noreferrer">
        {inner}
      </a>
    );
  }
  return <Link to={href}>{inner}</Link>;
}

/* ------------------------------------------------------------------ */
/*  Page                                                               */
/* ------------------------------------------------------------------ */

export function DashboardV2() {
  const queryClient = useQueryClient();

  // Reuses the same query keys as DashboardV1 and the layout sidebar,
  // so React Query serves from the shared cache — no duplicate fetches.
  const configs = useQuery({
    queryKey: ['configs'],
    queryFn: () => apiFetch<ConfigSurface[]>('/configs'),
  });
  const flags = useQuery({
    queryKey: ['flags'],
    queryFn: () => apiFetch<Flag[]>('/flags'),
  });
  const audit = useQuery({
    queryKey: ['audit', 'recent'],
    queryFn: () => apiFetch<AuditResponse>('/audit?limit=8'),
  });
  // Poll health every 30s so operators see degraded / error states within
  // one refresh cycle. Shared cache key with DashboardV1's health query.
  const health = useQuery({
    queryKey: ['health'],
    queryFn: () => apiFetch<HealthResponse>('/health'),
    refetchInterval: 30_000,
  });

  const isLoading = configs.isLoading || flags.isLoading || audit.isLoading;
  if (isLoading) return <DashboardSkeleton />;

  // Surface a single aggregate error if ANY dashboard query failed —
  // individual rows would each need their own fallbacks otherwise.
  const firstError = configs.error ?? flags.error ?? audit.error;
  if (firstError) {
    return (
      <div className="flex h-full flex-col">
        <DashboardTopbar
          title="Dashboard"
          subtitle="Operations Overview"
          isLive={false}
          onRefresh={() =>
            queryClient.invalidateQueries({
              predicate: (q) =>
                ['configs', 'flags', 'audit'].includes(q.queryKey[0] as string),
            })
          }
        />
        <div className="flex flex-1 items-center justify-center p-6">
          <Card className="max-w-md">
            <CardHeader>
              <CardTitle className="text-sm text-red-400">
                Dashboard data unavailable
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-xs text-neutral-400">
                {firstError instanceof Error
                  ? firstError.message
                  : 'One or more dashboard queries failed. Check your connection and try again.'}
              </p>
              <p className="mt-2 text-[11px] text-neutral-600">
                Hit Refresh in the topbar to retry.
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  const enabledFlags = flags.data?.filter((f) => f.enabled).length ?? 0;
  const totalFlags = flags.data?.length ?? 0;
  const surfaceCount = configs.data?.length ?? 0;
  const lastRefetch = Math.max(
    configs.dataUpdatedAt,
    flags.dataUpdatedAt,
    audit.dataUpdatedAt,
  );
  const isLive = Date.now() - lastRefetch < 60_000;

  return (
    <div className="flex h-full flex-col">
      <DashboardTopbar
        title="Dashboard"
        subtitle="Operations Overview"
        isLive={isLive}
        onRefresh={() =>
          queryClient.invalidateQueries({
            predicate: (q) =>
              ['configs', 'flags', 'audit'].includes(q.queryKey[0] as string),
          })
        }
      />

      <div className="flex-1 space-y-6 p-6">
        {/* Row 1 — Status cards */}
        <div
          className="dashboard-row grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4"
          style={{ ['--stagger' as string]: 1 } as React.CSSProperties}
        >
          <StatusCard title="Active Agents" icon={Bot} iconColor="purple" href="/agents">
            <p className="text-sm text-neutral-500">
              Summary coming in DASH-12
            </p>
            <p className="mt-1 text-[11px] text-neutral-600">Wave 2</p>
          </StatusCard>

          <StatusCard title="Config Surfaces" icon={Layers} iconColor="blue" href="/configs">
            <p className="font-mono text-xl font-semibold text-neutral-100">
              {surfaceCount}
            </p>
            <p className="text-xs text-neutral-500">registered</p>
          </StatusCard>

          <StatusCard title="Feature Flags" icon={ToggleLeft} iconColor="green" href="/flags">
            <p className="font-mono text-xl font-semibold text-neutral-100">
              {enabledFlags}/{totalFlags}
            </p>
            <p className="text-xs text-neutral-500">
              {enabledFlags} enabled
            </p>
          </StatusCard>

          <StatusCard
            title="System Health"
            icon={ShieldCheck}
            iconColor="green"
            href={`${API_BASE}/health`}
          >
            <HealthPill health={health.data} error={health.error} />
            <p className="mt-2 text-[11px] text-neutral-500">
              {health.data
                ? `${health.data.tables} tables · ${health.data.environment}`
                : 'Polling /api/health'}
            </p>
          </StatusCard>
        </div>

        {/* Row 2 — Service Health Bar placeholder (Wave 2: DASH-17/18) */}
        <div
          className="dashboard-row"
          style={{ ['--stagger' as string]: 2 } as React.CSSProperties}
        >
          <Card>
            <CardContent className="flex items-center justify-between p-4">
              <span className="text-xs font-semibold uppercase tracking-wider text-neutral-500">
                System Health
              </span>
              <span className="text-xs text-neutral-600">
                Per-service latencies coming in DASH-17/DASH-18 (Wave 2)
              </span>
            </CardContent>
          </Card>
        </div>

        {/* Row 3 — Flag Governance Widgets */}
        <div
          className="dashboard-row grid grid-cols-1 gap-4 lg:grid-cols-3"
          style={{ ['--stagger' as string]: 3 } as React.CSSProperties}
        >
          <FlagHealthCard flags={flags.data} isLoading={flags.isLoading} />
          <FlagLifecycleCard flags={flags.data} isLoading={flags.isLoading} />
          <FlagRecentChanges />
        </div>

        {/* Row 4 — Agent Governance */}
        <div
          className="dashboard-row grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4"
          style={{ ['--stagger' as string]: 4 } as React.CSSProperties}
        >
          <GovernanceSessionsCard />
          <GovernanceGatesCard />
          <GovernanceBudgetCard />
          <GovernanceModerationCard />
        </div>

        {/* Row 5 — Quick Actions + Activity Feed */}
        <div
          className="dashboard-row grid grid-cols-1 gap-4 lg:grid-cols-[1fr_1.8fr]"
          style={{ ['--stagger' as string]: 5 } as React.CSSProperties}
        >
          <QuickActionsCard />
          <ActivityFeed entries={audit.data?.entries ?? []} maxItems={8} />
        </div>

        {/* Row 6 — Metrics placeholder (Wave 3: DASH-19 through DASH-22) */}
        <div
          className="dashboard-row"
          style={{ ['--stagger' as string]: 6 } as React.CSSProperties}
        >
          <Card>
            <CardContent className="flex items-center justify-center gap-3 p-6">
              <span className="text-xs text-neutral-500">
                Agent Cost · Config Versions · Prompt Cache Savings metrics
                coming in Wave 3 (DASH-19 to DASH-22)
              </span>
            </CardContent>
          </Card>
        </div>
      </div>

      <ExternalLinksFooter />
    </div>
  );
}
